import React, { useState } from 'react';
import styles from './FileUpload.module.css';
import Button from '../Common/Button/Button.js';
import UploadIcon from '../icons/UploadIcon.js';
import { useTranslation } from 'react-i18next';

function FileUpload({ onFileSelect }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const { t } = useTranslation();

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  const MAX_FILES = 5;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const remainingSlots = MAX_FILES - selectedFiles.length;
    const filesToAdd = fileArray.slice(0, remainingSlots);
    let newFilesAdded = 0;

    filesToAdd.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        setErrorMessage(t('fileUpload.invalidFileType'));
        return;
      }

      const fileExists = selectedFiles.some(
        (f) => f.file.name === file.name && f.file.size === file.size,
      );
      if (fileExists) return;

      const reader = new FileReader();
      const fileId = Date.now() + Math.random().toString(36);
      reader.onload = (e) => {
        const newFile = { file, previewUrl: e.target.result, id: fileId };
        setSelectedFiles((prev) => {
          const updated = [...prev, newFile];
          onFileSelect?.(updated.map((f) => f.file));

          if (updated.length >= MAX_FILES) {
            setErrorMessage(t('fileUpload.maxFilesReached', { max: MAX_FILES }));
          } else {
            setErrorMessage(null);
          }
          return updated;
        });
      };
      reader.readAsDataURL(file);
      newFilesAdded++;
    });

    if (fileArray.length > remainingSlots) {
      setErrorMessage(t('fileUpload.maxFilesLimit', { max: MAX_FILES }));
    } else if (selectedFiles.length + newFilesAdded >= MAX_FILES) {
      setErrorMessage(t('fileUpload.maxFilesReached', { max: MAX_FILES }));
    } else {
      setErrorMessage(null);
    }
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDragLeave = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      handleFileSelect(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);
      onFileSelect?.(updated.map((f) => f.file));
      return updated;
    });
    setErrorMessage(null);
  };

  const uploadAreaClass = `${styles.uploadArea}`;
  const buttonsContainerClass = `${styles.buttonsContainer} font-small`;
  const maxReached = selectedFiles.length >= MAX_FILES;

  return (
    <div>
      <div
        className={uploadAreaClass}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <UploadIcon />
        <div className='mt-3'>{t('fileUpload.title')}</div>

        <input
          type='file'
          id='camera-input'
          accept='.jpg,.jpeg,.png'
          capture='environment'
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        <input
          type='file'
          id='gallery-input'
          accept='.jpg,.jpeg,.png'
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        <div className={buttonsContainerClass}>
          <Button
            type='button'
            size='small'
            text={t('fileUpload.takePhoto')}
            color='#fff'
            width='70%'
            onClick={() => document.getElementById('camera-input').click()}
            disabled={maxReached}
          />
          <Button
            type='button'
            size='small'
            text={t('fileUpload.chooseFromGallery')}
            backgroundColor='#fff'
            width='70%'
            onClick={() => document.getElementById('gallery-input').click()}
            disabled={maxReached}
          />
        </div>

        {errorMessage && (
          <div className='errorLabel position-relative mt-2 mb-2'>{errorMessage}</div>
        )}

        <div className='text-secondary font-small'>
          {selectedFiles.length === 0
            ? t('fileUpload.noFileSelected')
            : selectedFiles.length === 1
            ? t('fileUpload.selectedFiles', { count: selectedFiles.length })
            : t('fileUpload.selectedFiles_plural', { count: selectedFiles.length })}
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div className='mt-3'>
          {selectedFiles.map((fileItem) => (
            <div key={fileItem.id} className={styles.fileListItem}>
              <div className={styles.fileIcon}>
                <img
                  src={fileItem.previewUrl}
                  alt={fileItem.file.name}
                  className={styles.filePreviewImage}
                />
              </div>
              <div className={styles.fileInfo}>
                <div className='semi-bold font-small text-truncate'>{fileItem.file.name}</div>
                <div className='text-secondary font-extra-small'>
                  {formatFileSize(fileItem.file.size)}
                </div>
              </div>
              <button
                type='button'
                className={styles.removeFileBtn}
                onClick={() => removeFile(fileItem.id)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileUpload;
